import { useState, type ReactNode } from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface BlockProps {
  title: string;
  children: ReactNode;
  hideHeader?: boolean;
  alwaysOpen?: boolean;
  headerActions?: ReactNode;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  marginBottom: 8,
  width: "100%",

  "& .block-header": {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: theme.palette.action.hover,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },

  "& .block-header-always-open": {
    cursor: "default",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  "& .block-title": {
    flex: 1,
    fontWeight: "medium",
  },

  "& .block-content": {
    padding: "16px",
  },
}));

export function Block({
  title,
  children,
  hideHeader = false,
  alwaysOpen = false,
  headerActions,
}: BlockProps) {
  const [expanded, setExpanded] = useState(alwaysOpen);

  const handleExpandClick = () => {
    if (!alwaysOpen) {
      setExpanded(!expanded);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <StyledPaper>
      {!hideHeader ? (
        <>
          <Box
            className={`block-header ${alwaysOpen ? "block-header-always-open" : ""}`}
            onClick={handleExpandClick}
          >
            <Typography variant="subtitle1" className="block-title">
              {title}
            </Typography>

            {headerActions && (
              <Box
                onClick={handleActionClick}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                {headerActions}
              </Box>
            )}

            {!alwaysOpen && (
              <IconButton size="small" style={{ marginRight: -8 }}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>

          <Collapse in={alwaysOpen || expanded} timeout="auto" unmountOnExit>
            <div className="block-content">{children}</div>
          </Collapse>
        </>
      ) : (
        <div className="block-content">{children}</div>
      )}
    </StyledPaper>
  );
}
